/**
 * The interface language (#445): English or Hebrew.
 *
 * The signed-in learner's `ui_language` decides; before login the choice is
 * remembered in localStorage (a per-viewer convenience, never learner data).
 * Anything other than 'he' is English. Sets `<html lang dir>`; Hebrew is RTL.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../api/query-keys.js';
import { useApi } from '../app/api-context.js';
import type { Catalogue } from './messages.js';

export type UiLocale = 'en' | 'he';

const STORAGE_KEY = 'davinci.uiLanguage';

export function toLocale(value: unknown): UiLocale {
  return value === 'he' ? 'he' : 'en';
}

function readStored(): UiLocale {
  try {
    return toLocale(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'en';
  }
}

function writeStored(locale: UiLocale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Storage may be blocked; the choice then lasts for the session only.
  }
}

export interface LocaleContextValue {
  readonly locale: UiLocale;
  readonly setLocale: (locale: UiLocale) => void;
  /** The signed-in shell turns the profile read on; the sign-in pages turn it off (they make no request). */
  readonly setProfileEnabled: (enabled: boolean) => void;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en',
  setLocale: () => undefined,
  setProfileEnabled: () => undefined,
});

export function LocaleProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const [override, setOverride] = useState<UiLocale | null>(null);
  const [stored, setStored] = useState<UiLocale>(readStored);
  const [profileEnabled, setProfileEnabled] = useState(false);

  const profile = useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => api.profile(),
    retry: false,
    enabled: profileEnabled,
  });
  const update = useMutation({
    mutationFn: (uiLanguage: UiLocale) => api.updateProfile({ uiLanguage }),
    onSuccess: (data) => queryClient.setQueryData(queryKeys.profile, data),
  });

  const signedIn = profile.isSuccess;
  const locale: UiLocale = override ?? (profile.isSuccess ? toLocale(profile.data.uiLanguage) : stored);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'he' ? 'rtl' : 'ltr';
  }, [locale]);

  const { mutate } = update;
  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale: (next) => {
        setOverride(next);
        setStored(next);
        writeStored(next);
        if (signedIn) mutate(next);
      },
      setProfileEnabled,
    }),
    [locale, signedIn, mutate],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

export function useMessages<T>(catalogue: Catalogue<T>): T {
  const { locale } = useLocale();
  return catalogue[locale] as T;
}

/** Called by a layout: whether the learner's profile (and so `ui_language`) may be read from the server. */
export function useProfileLocale(enabled: boolean): void {
  const { setProfileEnabled } = useLocale();
  useEffect(() => {
    setProfileEnabled(enabled);
  }, [enabled, setProfileEnabled]);
}
